import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { genderColor, getAvatarHTML } from '../lib/avatars';

export default function ConnectionWindow({ window: cw, session, onAccept, onDecline }) {
  const [secondsLeft, setSecondsLeft] = useState(600);
  const [loading, setLoading] = useState(false);

  const myId = session?.user?.id;
  const isReceiver = cw.receiver_id === myId;

  useEffect(() => {
    const exp = new Date(cw.expires_at).getTime();
    const update = () => {
      const diff = Math.max(0, Math.floor((exp - Date.now()) / 1000));
      setSecondsLeft(diff);
      if (diff === 0) onDecline(cw.id, 'expired');
    };
    update();
    const iv = setInterval(update, 1000);
    return () => clearInterval(iv);
  }, [cw.expires_at, cw.id, onDecline]);

  const accept = async () => {
    setLoading(true);
    await supabase.from('connection_windows').update({ status: 'accepted' }).eq('id', cw.id);
    onAccept(cw);
    setLoading(false);
  };

  const decline = async () => {
    await supabase.from('connection_windows').update({ status: 'declined' }).eq('id', cw.id);
    onDecline(cw.id, 'declined');
  };

  const m = Math.floor(secondsLeft / 60);
  const s = secondsLeft % 60;
  const pct = (secondsLeft / 600) * 100;
  const isUrgent = secondsLeft <= 60;
  const isCritical = secondsLeft <= 30;
  const color = genderColor(cw.sender?.gender || 'Other');
  const avatarSvg = getAvatarHTML(cw.sender?.gender || 'Man', cw.sender_id, 44);

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:400,
      background:'rgba(0,0,0,0.82)',
      backdropFilter:'blur(8px)',
      display:'flex', alignItems:'center', justifyContent:'center',
      padding:'20px',
      animation:'fadeIn 0.3s ease',
    }}>
      <style>{`
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes popIn { from{opacity:0;transform:scale(0.92) translateY(16px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes pulseRing { 0%{transform:scale(1);opacity:0.5} 100%{transform:scale(1.9);opacity:0} }
        @keyframes shake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-3px)} 75%{transform:translateX(3px)} }
        @keyframes urgentPulse { 0%,100%{opacity:1} 50%{opacity:0.6} }
      `}</style>

      <div style={{
        background:'#0d0d0d',
        border:`1px solid ${isUrgent ? 'rgba(255,68,68,0.3)' : 'rgba(255,255,255,0.1)'}`,
        borderRadius:'24px',
        padding:'28px 24px',
        maxWidth:'360px',
        width:'100%',
        animation:'popIn 0.4s cubic-bezier(0.16,1,0.3,1)',
        boxShadow:`0 24px 80px rgba(0,0,0,0.8), 0 0 0 1px ${isUrgent ? 'rgba(255,68,68,0.1)' : 'transparent'}`,
      }}>

        {/* Header profil */}
        <div style={{display:'flex', alignItems:'center', gap:'13px', marginBottom:'16px'}}>
          <div style={{position:'relative', flexShrink:0}}>
            <div style={{
              width:'52px', height:'52px', borderRadius:'50%',
              background:'rgba(8,8,8,0.9)',
              border:`2px solid ${color}`,
              display:'flex', alignItems:'center', justifyContent:'center',
            }} dangerouslySetInnerHTML={{__html: avatarSvg.match(/<svg[^>]*>[\s\S]*<\/svg>/)?.[0] || ''}}/>
            {/* Ring animé */}
            <div style={{
              position:'absolute', inset:'-8px', borderRadius:'50%',
              border:`1.5px solid ${color}`,
              animation:'pulseRing 1.8s ease-out infinite',
              opacity:0.4,
            }}/>
            <div style={{
              position:'absolute', bottom:'1px', right:'1px',
              width:'12px', height:'12px', borderRadius:'50%',
              background:'#00d68f', border:'2px solid #0d0d0d',
            }}/>
          </div>
          <div>
            <div style={{fontWeight:700, color:'white', fontSize:'1rem', letterSpacing:'-0.3px'}}>
              {cw.sender?.name || 'Someone'}{cw.sender?.age ? `, ${cw.sender.age}` : ''}
            </div>
            <div style={{color:'rgba(255,255,255,0.3)', fontSize:'0.75rem', marginTop:'2px'}}>
              {cw.sender?.gender} · wants to connect
            </div>
          </div>
          <div style={{
            marginLeft:'auto',
            padding:'4px 10px',
            background:'rgba(255,149,0,0.1)',
            border:'1px solid rgba(255,149,0,0.25)',
            borderRadius:'100px',
            fontSize:'0.65rem',
            color:'rgba(255,149,0,0.9)',
            fontWeight:600,
            whiteSpace:'nowrap',
          }}>⚡ 10 min</div>
        </div>

        {/* Message */}
        {cw.message && (
          <div style={{
            background:'rgba(255,255,255,0.04)',
            border:'1px solid rgba(255,255,255,0.07)',
            borderRadius:'14px',
            padding:'12px 14px',
            marginBottom:'16px',
            color:'rgba(255,255,255,0.55)',
            fontSize:'0.88rem',
            lineHeight:1.6,
            fontStyle:'italic',
          }}>
            "{cw.message}"
          </div>
        )}

        {/* Timer */}
        <div style={{marginBottom:'18px'}}>
          <div style={{
            display:'flex', justifyContent:'space-between', alignItems:'center',
            marginBottom:'7px',
          }}>
            <span style={{
              fontSize:'0.6rem',
              color: isUrgent ? 'rgba(255,68,68,0.7)' : 'rgba(255,255,255,0.2)',
              textTransform:'uppercase',
              letterSpacing:'1.5px',
              animation: isUrgent ? 'urgentPulse 1s infinite' : 'none',
            }}>
              {isUrgent ? '⚠ Expires soon' : 'Connection window closes in'}
            </span>
            <span style={{
              fontSize:'1.4rem',
              fontWeight:800,
              color: isCritical ? '#ff4444' : isUrgent ? '#ff9500' : 'white',
              letterSpacing:'-1px',
              animation: isCritical ? 'shake 0.4s ease infinite' : 'none',
            }}>
              {m}:{s.toString().padStart(2,'0')}
            </span>
          </div>

          {/* Barre de progression */}
          <div style={{
            height:'3px',
            background:'rgba(255,255,255,0.07)',
            borderRadius:'100px',
            overflow:'hidden',
          }}>
            <div style={{
              height:'100%',
              width:`${pct}%`,
              background: isCritical
                ? '#ff4444'
                : isUrgent
                  ? 'linear-gradient(90deg,#ff9500,#ff4444)'
                  : `linear-gradient(90deg,${color},#00d68f)`,
              borderRadius:'100px',
              transition:'width 1s linear, background 0.5s ease',
            }}/>
          </div>

          <div style={{
            marginTop:'6px',
            fontSize:'0.68rem',
            color:'rgba(255,255,255,0.18)',
            textAlign:'center',
          }}>
            After that, this connection disappears forever.
          </div>
        </div>

        {/* Boutons */}
        {isReceiver ? (
          <div style={{display:'flex', gap:'8px'}}>
            <button onClick={accept} disabled={loading || secondsLeft === 0} style={{
              flex:1, padding:'13px',
              background: secondsLeft === 0 ? 'rgba(255,255,255,0.05)' : 'white',
              color: secondsLeft === 0 ? 'rgba(255,255,255,0.2)' : '#080808',
              border:'none', borderRadius:'13px',
              fontWeight:700, fontSize:'0.92rem',
              cursor: secondsLeft === 0 ? 'not-allowed' : 'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', gap:'7px',
            }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M12 7L2 2L4.5 7L2 12L12 7Z" fill="currentColor"/></svg>
              {loading ? '...' : 'Connect · Now'}
            </button>
            <button onClick={decline} style={{
              width:'46px', height:'46px', borderRadius:'13px',
              background:'rgba(255,255,255,0.05)',
              border:'1px solid rgba(255,255,255,0.08)',
              color:'rgba(255,255,255,0.3)',
              cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:'18px',
            }}>×</button>
          </div>
        ) : (
          <div style={{
            textAlign:'center', padding:'8px',
            color:'rgba(255,255,255,0.3)', fontSize:'0.82rem',
          }}>
            Waiting for {cw.receiver?.name || 'them'} to respond...
          </div>
        )}
      </div>
    </div>
  );
}
